/**
 * Générateur — Attendu 184 — O2 « Il se repère dans un pavé droit. »
 * Niveau : 4ème | Thème #10 Espace (O2) | Domaine : Espace et géométrie.
 * Source prof : « TANOED EXERCISEUR ESPACE ET GEOMETRIE 4EME », O2
 * (Ex.4 placer/lire des points 3D, Ex.5 faces, Ex.6 milieu, coordonnées
 * communes). Figure canonique = l'image « Exemples de réussite » (repère de
 * l'espace dans un pavé).
 *
 * ADAPTATION SIGNALÉE (note « Correcteur » du doc) : pas de placement 3D
 * interactif dans l'app ; « place (2 ; 3 ; 4) » devient un QCM d'images.
 *
 * Revue 2026-09-23 (vague 4), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 9) : « Il
 * se repère dans un pavé droit. » ; exemples de réussite : « Dans un repère
 * de l'espace, il lit les coordonnées d'un point et place un point de
 * coordonnées données. », la figure du pavé ABCDEFGH (A origine, [AB) sur la
 * fuyante, [AD) horizontale, [AE) verticale, « 1 » sur chaque axe), « les
 * coordonnées de A, H et L » (L sur l'arête [BC]) et « place le point de
 * coordonnées (2 ; 3 ; 4) ». Cours du professeur (19Espace2 p. 4-5) : « on
 * note dans l'ordre entre parenthèses l'abscisse, l'ordonnée et
 * l'altitude » ; A(0 ; 0 ; 0) … G(5 ; 7 ; 4), K milieu de [FG] =
 * (5 ; 3,5 ; 4).
 *   - La figure est celle du cours, partagée avec 183 (svgRepereEspace,
 *     svgEspace.js) : trois axes gradués depuis A, l'abscisse sur la FUYANTE,
 *     l'ordonnée sur l'horizontale, l'altitude sur la verticale, « 0 » et
 *     « 1 » seulement (À ARBITRER : le cours écrit aussi 5, 7, 4 au bout des
 *     axes, ce sont les coordonnées de B, D, E) ; X de 2 à 4, Y de 3 à 7, Z
 *     de 2 à 5, deux à deux distinctes. Plus aucune COTE : l'élève recopiait
 *     des longueurs écrites sur la figure dans 100 % des tirages, et la cote
 *     de profondeur était posée sur l'arête cachée [DC] (100 %, à moins de
 *     4 px dans 46,0 %). La convention inverse du cours ([AB) horizontale) et
 *     sa phrase CONVENTION, répétée à chaque tirage, sont retirées : les axes
 *     colorés et fléchés la portent.
 *   - « Lire les coordonnées (pavé) » (lire_texte_trou_saisie) : GARDÉE,
 *     recadrée : deux points, un sommet (A une fois sur quatre : le plafond
 *     demande A, H et L) et un point d'arête (le L d'Eduscol, le K du cours),
 *     une ligne KaTeX par point, « G( ⬚ ; ⬚ ; ⬚ ) ».
 *   - NOUVEAU « Lire les coordonnées (pavé) » en QCM (lire_qcm_unique) :
 *     « Quelles sont les coordonnées du point G ? », point à trois
 *     coordonnées distinctes non nulles (G ou un point de [FG], [CG], [GH]) ;
 *     choix : la clé, (y ; x ; z) echange_xy (DEPP 4e 2023 q.20, 35,9 %),
 *     (y ; z ; x) ordre_du_dessin, (0 ; y ; z) abscisse_oubliee. Jamais les
 *     trois transpositions : le vote par position les ramène à la clé.
 *   - « Milieu dans le pavé » (milieu_texte_trou_saisie) : RECADRÉE sur le
 *     milieu d'une ARÊTE (les diagonales faisaient 56,6 % des tirages),
 *     marqué d'une croix sur la figure, l'arête graduée, lu et non calculé :
 *     la phrase « (Les coordonnées du milieu sont les demi-sommes.) » SORT
 *     (formule de seconde, méthode soufflée, parenthèse). Demi-valeur quand
 *     l'arête a une longueur impaire ; la consigne impose l'écriture
 *     décimale (3,5 et non 7/2).
 *   - « Appartenance à une face » (face_qcm_unique) : SORTIE (aucune ligne
 *     du plafond, distracteurs au hasard), remplacée par « Placer un point »
 *     (placer_qcm_unique), QCM d'images, choix en imageData (§1) : « Sur quel
 *     dessin le point de coordonnées (2 ; 3 ; 4) est-il placé ? » ; quatre
 *     pavés IDENTIQUES, sans lettres, une même croix chacun ; le point et
 *     les trois distracteurs sont sur des ARÊTES (mode auto, À ARBITRER : un
 *     point intérieur n'a pas de lecture unique en perspective) : sur [FG],
 *     [CG] ou [GH] ; distracteurs : l'une puis l'autre des deux coordonnées
 *     fixes remise à 0 (altitude_oubliee, abscisse_oubliee ou
 *     ordonnee_oubliee) et la coordonnée libre décalée d'une graduation
 *     (ordonnee_decalee, altitude_decalee ou abscisse_decalee).
 *   - « Coordonnées communes » (communes_tableau_vf) : DÉPLACÉE vers 183
 *     (vocabulaire).
 *   - Rappel brouillon : aucun (lecture de figure).
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM). Aucun
 *     solveur test-side ne couvre ce gen (la phrase « le solveur relit les
 *     triplets » était fausse) ; la figure porte data-repere et data-points.
 * Quatre formats, dont deux QCM.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, tex,
} from '../../../utils';
import {
    svgRepereEspace, elementFigure, choixFigure, sommetsRepere, pointLisible, areteVisible, ARETES_VISIBLES,
    tirerDimsRepere, nommerPointsRepere, pointsAretesRepere, tripletLatex, tirerPointTroisValeurs,
} from './svgEspace';

const ATTENDU_ID = 184;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LIRE = 'Lire les coordonnées (pavé)';
const L_MILIEU = 'Milieu d\'une arête';
const L_PLACER = 'Placer un point';

const SOMMETS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/** La figure de l'énoncé : les points d'arête sont marqués d'une croix. */
const figure = (dims, pts) => {
    const { svg, alt } = svgRepereEspace({ ...dims, points: pts.filter((p) => p.arete).map((p) => ({ nom: p.nom, c: p.c })) });
    return elementFigure(svg, alt);
};

/** La ligne à trous d'un point, entièrement en KaTeX : G( ⬚ ; ⬚ ; ⬚ ). */
const ligneTrous = (p) => `$${p.nom}(\\,${p.c.map((v) => `{{${String(v).replace('.', ',')}}}`).join('\\,;\\,')}\\,)$`;

// ═════════ Carte « lire » : saisie ═════════

const genLireSaisie = () => {
    const dims = tirerDimsRepere();
    const S = sommetsRepere(dims.X, dims.Y, dims.Z);
    const nomSommet = randInt(1, 4) === 1 ? 'A' : randomChoice(SOMMETS.slice(1));
    const sommet = { nom: nomSommet, c: S[nomSommet], arete: null };
    const q = randomChoice(pointsAretesRepere(dims));
    const pointArete = { nom: nommerPointsRepere(1)[0], c: q.c, arete: q.arete };
    const points = shuffle([sommet, pointArete]);
    const phrase = points.map(ligneTrous).join('\n');
    const texte = `<p>Le point ${pointArete.nom} est sur l'arête [${pointArete.arete}].</p>`
        + `<p><b>Lis sur la figure les coordonnées des points ${points[0].nom} et ${points[1].nom}.</b></p>`;
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_LIRE), mode: 'saisie', elements_additionnels: figure(dims, [pointArete]) });
    payload._meta.params = { dims, sommet: sommet.nom, point: pointArete.nom, arete: pointArete.arete, coordonnees: pointArete.c };
    return payload;
};

// ═════════ Carte « lire » : QCM ═════════

const genLireQcm = () => {
    // L'ordre des trois valeurs est tiré d'abord : sans lui, l'option « petite
    // abscisse, grande ordonnée » était presque toujours la clé (plages du pavé).
    const t = tirerPointTroisValeurs();
    const { dims } = t;
    const p = t.sommet ? { nom: 'G', c: t.c, arete: null } : { ...t, nom: nommerPointsRepere(1)[0] };
    const [x, y, z] = p.c;
    if (new Set([x, y, z]).size !== 3 || [x, y, z].includes(0)) throw new Error('gen_184 lire_qcm : coordonnées');
    const options = [
        { c: [x, y, z], code: null },
        { c: [y, x, z], code: 'echange_xy' },
        { c: [y, z, x], code: 'ordre_du_dessin' },
        { c: [0, y, z], code: 'abscisse_oubliee' },
    ];
    // Second chemin : quatre triplets distincts, un seul égal au point.
    if (new Set(options.map((o) => o.c.join(';'))).size !== 4) throw new Error('gen_184 lire_qcm : options');
    const choix = options.map((o) => tex(tripletLatex(o.c)));
    const texte = (p.arete ? `<p>Le point ${p.nom} est sur l'arête [${p.arete}].</p>` : '')
        + `<p><b>Quelles sont les coordonnées du point ${p.nom} ?</b></p>`;
    const payload = makeQcm(choix, choix[0], texte, { ...opts(L_LIRE), elements_additionnels: figure(dims, [p]) });
    payload._meta.params = { dims, point: p.nom, coordonnees: p.c, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// ═════════ Carte « milieu » : saisie ═════════

const genMilieu = () => {
    const dims = tirerDimsRepere();
    const { X, Y, Z } = dims;
    const S = sommetsRepere(X, Y, Z);
    const cands = ARETES_VISIBLES.map((a) => {
        const P = S[a[0]]; const Q = S[a[1]];
        return { arete: a, c: P.map((v, k) => (v + Q[k]) / 2) };
    }).filter((m) => pointLisible(X, Y, Z, m.c));
    const m = randomChoice(cands);
    // Second chemin : le milieu est à égale distance des deux extrémités, coordonnée par coordonnée.
    const P = S[m.arete[0]]; const Q = S[m.arete[1]];
    if (m.c.some((v, k) => v - P[k] !== Q[k] - v)) throw new Error('gen_184 milieu : milieu');
    const M = { nom: nommerPointsRepere(1)[0], c: m.c, arete: m.arete };
    const texte = `<p>Le point ${M.nom} est le milieu de l'arête [${M.arete}].</p>`
        + `<p><b>Lis sur la figure les coordonnées du point ${M.nom}.</b> Écris-les en écriture décimale.</p>`;
    const payload = makeTexteTrou(ligneTrous(M), texte,
        { ...opts(L_MILIEU), mode: 'saisie', elements_additionnels: figure(dims, [M]) });
    payload._meta.params = { dims, point: M.nom, arete: M.arete, coordonnees: M.c, demi: M.c.some((v) => !Number.isInteger(v)) };
    return payload;
};

// ═════════ Carte « placer » : QCM d'images ═════════

/**
 * Les trois arêtes issues de G (deux coordonnées au maximum) portent le point :
 * libre = l'indice de la coordonnée qui varie le long de l'arête.
 */
const ARETES_PLACER = [
    { arete: 'FG', libre: 1, decale: 'ordonnee_decalee' },
    { arete: 'CG', libre: 2, decale: 'altitude_decalee' },
    { arete: 'GH', libre: 0, decale: 'abscisse_decalee' },
];
const OUBLI = ['abscisse_oubliee', 'ordonnee_oubliee', 'altitude_oubliee'];

const tirerPlacer = (dims) => {
    const { X, Y, Z } = dims;
    const S = sommetsRepere(X, Y, Z);
    const cands = [];
    ARETES_PLACER.forEach((a) => {
        const n = S.G[a.libre];
        for (let k = 1; k < n; k++) {
            const c = [...S.G]; c[a.libre] = k;
            [k - 1, k + 1].filter((d) => d >= 1 && d < n).forEach((d) => {
                const decale = [...c]; decale[a.libre] = d;
                const oublis = [0, 1, 2].filter((j) => j !== a.libre).map((j) => { const o = [...c]; o[j] = 0; return { c: o, code: OUBLI[j] }; });
                const tous = [{ c, code: null }, { c: decale, code: a.decale }, ...oublis];
                if (tous.every((t) => pointLisible(X, Y, Z, t.c, true))) cands.push({ arete: a.arete, tous });
            });
        }
    });
    return cands;
};

const genPlacer = () => {
    let dims = null; let cands = [];
    for (let essai = 0; essai < 30 && cands.length === 0; essai++) {
        dims = tirerDimsRepere();
        cands = tirerPlacer(dims);
    }
    if (cands.length === 0) { dims = { X: 2, Y: 5, Z: 4 }; cands = tirerPlacer(dims); }   // le pavé d'Eduscol, vérifié
    const t = randomChoice(cands);
    const [cle] = t.tous;
    // Second chemin : quatre points distincts, un seul au triplet demandé, chacun sur une arête.
    if (new Set(t.tous.map((q) => q.c.join(';'))).size !== 4) throw new Error('gen_184 placer : points');
    // Quatre images du MÊME pavé : les mêmes arêtes graduées (celles des quatre croix) et
    // des étiquettes placées loin des quatre croix, pour que seule la croix change.
    const graduer = [...new Set(t.tous.map((q) => areteVisible(dims.X, dims.Y, dims.Z, q.c)))];
    const eviter = t.tous.map((q) => q.c);
    const images = t.tous.map((q) => choixFigure(svgRepereEspace({
        ...dims, points: [{ nom: '', c: q.c }], sommets: false, choix: true, graduer, eviter,
    }).svg));
    const texte = `<p><b>Sur quel dessin le point de coordonnées ${tex(tripletLatex(cle.c))} est-il placé ?</b></p>`;
    const payload = makeQcm(images, images[0], texte, opts(L_PLACER));
    payload._meta.params = { dims, arete: t.arete, coordonnees: cle.c, distracteurs: t.tous.slice(1).map((q) => q.code) };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'figure du cours (svgRepereEspace, partagée avec 183) : X de 2 à 4 sur la fuyante, Y de 3 à 7, Z de 2 à 5, deux à deux distinctes ; « 0 » et « 1 » seuls écrits ; jamais de cote',
        'un point hors des sommets est une croix sur une arête VISIBLE, loin des croisements de la projection (16 unités, 20 dans une image de choix) ; son arête est graduée',
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs) ; quatre options distinctes vérifiées (throw sinon)',
    ],
    lire_qcm_unique: {
        support: 'G, ou un point de [FG], [CG] ou [GH] : trois coordonnées distinctes et non nulles (x ; y ; z)',
        familles: {
            echange_xy: '(y ; x ; z) : abscisse et ordonnée échangées (DEPP 4e 2023 q.20 : 35,9 % sur le plan), PRINCIPALE',
            ordre_du_dessin: '(y ; z ; x) : l\'horizontale, puis la verticale, puis la fuyante, l\'habitude du plan (inférée)',
            abscisse_oubliee: '(0 ; y ; z) : la fuyante ignorée, le point lu sur la face du fond',
        },
        supprimees: {
            trois_transpositions: '(y ; x ; z), (x ; z ; y), (z ; y ; x) : la valeur majoritaire de chaque position redonne la clé à coup sûr',
            z_x_y: '(z ; x ; y) : aucune erreur attestée ne l\'écrit',
        },
        residu: 'le vote par position donne (y ; y ; z), qui n\'est pas une option ; l\'option la plus proche du vote est ex aequo entre la clé, echange_xy et abscisse_oubliee (mesuré par le test jetable)',
    },
    placer_qcm_unique: {
        support: 'le point sur [FG], [CG] ou [GH] (deux coordonnées au maximum), sa coordonnée libre entière et à une graduation au moins de chaque bout ; quatre images du MÊME pavé, sans lettres, une croix chacune',
        familles: {
            altitude_oubliee: 'l\'altitude remise à 0 : le point du dessous, sur la base (point de [FG] ou de [GH])',
            abscisse_oubliee: 'l\'abscisse remise à 0 : le point du fond (point de [FG] ou de [CG])',
            ordonnee_oubliee: 'l\'ordonnée remise à 0 : le point de la face de gauche (point de [CG] ou de [GH])',
            ordonnee_decalee: 'sur [FG], une graduation à côté : les graduations mal comptées',
            altitude_decalee: 'sur [CG], une graduation à côté',
            abscisse_decalee: 'sur [GH], une graduation à côté',
        },
        supprimees: {
            permutation: '(3 ; 2 ; 4) pour (2 ; 3 ; 4) : sur des arêtes, le permuté n\'est pas dessinable',
        },
    },
};

// ═════════ Registre et exports ═════════

const GENERATORS = {
    lire_texte_trou_saisie: genLireSaisie,
    lire_qcm_unique: genLireQcm,
    milieu_texte_trou_saisie: genMilieu,
    placer_qcm_unique: genPlacer,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const LABELS_PAR_CLE = { placer_qcm_unique: 'QCM images' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: LABELS_PAR_CLE[k] || FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'milieu', label: L_MILIEU, formatsList: buildFormats('milieu') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { GENERATORS };
